/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useEffect, useState } from "react";
import { Dropdown, Menu, Popover } from "antd";
import classNames from "classnames";
import { ApolloClient, NormalizedCacheObject, useLazyQuery, useQuery } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "@pravaler/amigoedu-ui/dist/components/Auth";
import { useRouter } from "next/router";
import Link from "next/link";

import { ME, USER_CHECKOUTS, FETCH_FULL_CITIES } from "./graphql";
import style from "./style.module.scss";

import MyInterestsList from "../MyInterestsList";
import { PromotionBar } from "../PromotionBar";
import { Arrow } from "../Arrow";

// Utility Functions
import { useMedia } from "../../hooks/useMedia";
import { myInterests, showBuscaNotaBanners } from "../../vendor/ABTesting";
import { createEvent } from "../../vendor/eventTracker";

import { ESTACIO_END_DATE } from "../../constants/campaingDates";

// Assets
import LogoDoctum from "../../assets/images/doctum-logo.webp";
import LogoEstacio from "../../assets/images/logo-estacio.webp";
import LogoIbmec from "../../assets/images/logo-ibmec.webp";
import LogoAmigoEdu from "../../assets/images/amigoedu-logo.webp";
import LogoPravaler from "../../assets/images/pravaler-logo.webp";
import { BuscaNotaIcon } from "../../assets/buscaNotaIcon";
import { Conversation } from "../../assets/conversation";
import { SignInAlt } from "../../assets/signinalt";
import { Bookmark } from "../../assets/bookmark";
import { Close } from "../../assets/close";
import { EstacioWeekPercentIcon } from "../../assets/EstacioWeekPercentIcon";

import { storage } from "@utils/storage";
import { getToken } from "@utils/auth";
import { parseJwt } from "@utils/formatters";
import { getUserEnemScore, isEstacioWeek } from "@utils/campaings";
import { initializeAuthApollo, useAuthApollo } from "@lib/apolloClientConfig";
import "antd/dist/antd.css";

interface IHeaderProps {
	onClickShowModal: (visible: boolean) => void;
	setShowSignInForm: (showSign: boolean) => void;
	setFixed: (fixed: boolean) => void;
	setShowContact: (visible: boolean) => void;
	fixHeader: boolean;
	basicMode: boolean;
	dontShowHeader?: boolean;
	transparent?: boolean;
	amigoPravaler?: boolean;
	customHeader?: "estacio" | "doctum" | "ibmec" | null;
	isLoggedIn?: boolean;
	onLogout: () => void;
}

export const Header = ({
	onClickShowModal,
	basicMode,
	setShowSignInForm,
	setFixed,
	setShowContact,
	fixHeader,
	dontShowHeader,
	transparent,
	customHeader,
	amigoPravaler,
	isLoggedIn,
	onLogout,
}: IHeaderProps): JSX.Element => {
	const [menu, setMenu] = useState(false);
	const router = useRouter();

	const hasWindow = typeof window !== "undefined";

	const inEnaePage = router.pathname.match(/(.*)\/enae(.*)/) !== null;
	const inBuscaNotaPage = router.pathname.match(/busca-nota/) !== null;
	const inContestPage = router.pathname.match(/(.*)\/concurso-bolsas(.*)/) !== null;
	const selectedPathnameCourse = router.pathname.match(/(.*)\/cursos\/(.*)/) !== null;
	const [hideAfterScroll, setHideAfterScroll] = useState(false);
	const [active, setActive] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [clicked, setClicked] = useState(false);
	const [wasScrolled, setWasScrolled] = useState(false);
	const { setUser, setStep } = useAuth();
	const [dataMe, setDataMe] = useState({ me: { id: "", name: "", avatar: null } });
	const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();

	useEffect(() => {
		const handleScroll = () => {
			setHideAfterScroll(window.scrollY > 190);
		};
		window.addEventListener("scroll", handleScroll);

		return () => window.removeEventListener("scroll", handleScroll);
	}, [hideAfterScroll]);

	const [checkouts, setCheckouts] = useState({
		checkouts: [],
		count: 0,
	});

	const [fetchCheckouts, { loading: loadingUserCheckouts, error: errorUserCheckouts, refetch: refetchUserCheckouts }] = useLazyQuery(
		USER_CHECKOUTS,
		{
			fetchPolicy: "no-cache",
			onCompleted: (res) => {
				setCheckouts(res?.userCheckouts);
			},
			variables: { limit: 5 },
			client,
		},
	);

	const [fetchUserData] = useLazyQuery(ME, {
		fetchPolicy: "no-cache",
		onCompleted: (res) => {
			setDataMe(res);
		},
		client,
	});

	useEffect(() => {
		if (isLoggedIn) {
			const client = initializeAuthApollo();
			setClient(client);
			fetchCheckouts();
			fetchUserData();
		}
	}, [isLoggedIn]);

	const handleTrigger = () => {
		setMenu(!menu);
	};

	const closeFixed = () => {
		setActive(false);
		setFixed(false);
	};
	const handleLogout = () => {
		const pageEnae = window.location.pathname === "/enae";
		storage.removeItem("user");
		client?.clearStore();

		onLogout();
		setMenu(!menu);
		setUser(null);
		setStep("login");
		closeFixed();
		if (pageEnae) {
			router.push("/enae");
		} else {
			if (hasWindow) {
				window.location.pathname = "/";
			}
		}
	};

	const isImpersonate = () => {
		if (isLoggedIn) {
			const token = getToken(storage);

			if (token) {
				const impersonate = parseJwt(token);

				if (impersonate.type === "IMPERSONATE") {
					return (
						<>
							Você está impersonado como <strong>{impersonate.user.name}</strong>.
						</>
					);
				}
			}
		}
	};

	const { data: dataCity } = useQuery(FETCH_FULL_CITIES, { client });

	const hideLoginMenu =
		(hasWindow && window.location.pathname === "/login") ||
		(hasWindow && window.location.pathname === "/registro") ||
		(hasWindow && window.location.pathname.includes("busca-nota"));

	const btnBackMenu = hasWindow && !!window.location.pathname.match(/(.*)\/cursos\/(.*)/);
	const isMobile = useMedia(["(max-width: 768px)"], [true], false);
	const hasDifferentClockColor: boolean = useMedia(["(max-width: 576px)"], [true], false);

	const user = ({ me }) => (
		<>
			<span data-cy="username">{me?.name}</span>
			<figure>
				<picture className={style.avatar}>
					{me?.avatar ? <img loading="lazy" src={me?.avatar} alt="Foto de avatar do usuário" /> : <div className={style.avatar}></div>}
				</picture>
			</figure>
		</>
	);

	const MyAccountLink = (label: string) => (
		<a
			href="https://amigoedu.com.br/minha-conta"
			onClick={() => {
				handleTrigger();
				closeFixed();
			}}
		>
			{label}
		</a>
	);

	const menuFloating = (
		<Menu className={style.menuDrop}>
			<Menu.Item key="0" className={style.myAccountLink}>
				{MyAccountLink("Minha Conta")}
			</Menu.Item>
			{dataMe && dataMe?.me && (
				<a href="https://amigoedu.com.br/minha-conta" className={style.resp}>
					{user(dataMe)}
				</a>
			)}
			<Menu.Divider />
			<Menu.Item key="3">
				<a
					onClick={() => {
						handleLogout();
						closeFixed();
					}}
				>
					<span>Sair</span>
				</a>
			</Menu.Item>
		</Menu>
	);

	const handleHoverChange = (visible: boolean) => {
		setHovered(visible);
		setClicked(false);
	};

	const hide = () => {
		setHovered(false);
		setClicked(false);
	};

	const handleClickChange = (visible: boolean) => {
		setHovered(false);
		setClicked(visible);
		createEvent("clicked_on_the_popover_my_interests");
		if (isMobile) {
			hide();
			router.push("https://amigoedu.com.br/meus-interesses");
		}
	};

	useEffect(() => {
		if (clicked && refetchUserCheckouts) {
			refetchUserCheckouts();
		}
	}, [clicked]);

	const submitHandler = (e: React.MouseEvent) => {
		e.preventDefault();
		router.push(
			`/busca?city=${dataCity?.fullCities?.selectedCity.city}&stateUF=${
				dataCity?.fullCities?.selectedCity.stateUF
			}&level[]=Graduacao&courseId=71b16606-fa33-4396-91da-6e33e2ea34d3&courseName=Enfermagem&page=1&type[]=INCLASS&sid=${uuidv4()}&source=${
				window.location.pathname === "/busca" ? "search_page" : "home"
			}&type[]=MIXED&type[]=ONLINE&noAutoScroll=false&promotion=true&promotionSlug=superenem`,
		);
		hide();
	};

	const menuNav = amigoPravaler ? (
		<>
			<div
				className={style.contactUsAmigoPravaler}
				onClick={() => {
					closeFixed();
					setShowContact(true);
				}}
			>
				<Conversation />
				<span>Fale com a gente</span>
			</div>
		</>
	) : (
		<>
			<a href="https://amigoedu.com.br/">
				<span>Início</span>
			</a>
			<a href="https://amigoedu.com.br/quem-somos">
				<span>Quem somos</span>
			</a>
			<a href="https://amigoedu.com.br/parceiros">
				<span>Parceiros</span>
			</a>
			<a href="https://amigoedu.com.br/blogdoamigo/">
				<span>Blog</span>
			</a>
			{isMobile && myInterests() && (
				<a href="https://amigoedu.com.br/meus-interesses">
					<div className={style.icon}>{<Bookmark />}</div>
					<span>Meus interesses</span>{" "}
					{checkouts?.checkouts?.length >= 1 && <div className={style.number}>{checkouts?.checkouts?.length}</div>}
				</a>
			)}
			<a
				className={style.contactUs}
				onClick={() => {
					closeFixed();
					setShowContact(true);
				}}
			>
				<Conversation />
				<span>Fale com a gente</span>
			</a>
		</>
	);

	useEffect(() => {
		if (checkouts?.checkouts?.length >= 1) {
			setHovered(true);
			setTimeout(() => {
				setHovered(false);
			}, 10000);
		}
	}, [loadingUserCheckouts]);

	const content = (
		<div>
			<div className={style.MyInterestsContent}>
				<div className={style.headerInterests}>
					<Bookmark />
					<h1>Meus Interesses</h1>
				</div>

				<MyInterestsList
					activateRedirect={submitHandler}
					checkouts={checkouts}
					loadingUserCheckouts={loadingUserCheckouts}
					refetchUserCheckouts={refetchUserCheckouts}
					errorUserCheckouts={errorUserCheckouts}
					hidePopover={hide}
					setWasScrolled={setWasScrolled}
					wasScrolled={wasScrolled}
				/>
			</div>
		</div>
	);

	const titlePopover =
		checkouts?.checkouts?.length >= 1 ? (
			<div className={style.popoverYellow}>
				<span>Você já selecionou algumas bolsas. Clique aqui e continue de onde parou.</span>
			</div>
		) : (
			<div className={style.popoverWhite}>
				<span>Meus interesses</span>
			</div>
		);

	const customHeaderVariant = (type: "estacio" | "ibmec" | "doctum") => (
		<div className={style.logosRow}>
			<div className={style.logo}>
				<figure>
					<picture>
						{type === "estacio" && <img loading="lazy" src={LogoEstacio} alt="logo da Universidade Estácio de Sá" />}
						{type === "ibmec" && <img loading="lazy" src={LogoIbmec} alt="logo da IBMEC" />}
						{type === "doctum" && <img loading="lazy" src={LogoDoctum} alt="logo da Doctum" />}
					</picture>
				</figure>
			</div>
		</div>
	);

	return (
		<>
			{isImpersonate() && <div className={style.impersonate}>{isImpersonate()}</div>}
			<header
				className={classNames(style.header, {
					[style.remove]: hideAfterScroll && (selectedPathnameCourse || inEnaePage || inBuscaNotaPage) && !fixHeader,
					[style.active]: active || fixHeader,
					[style.transparent]: transparent,
					[style.show]: dontShowHeader,
				})}
			>
				<div className={style.container}>
					<div
						className={classNames(style.containerAccount, {
							[style.gridClass]: btnBackMenu && isMobile,
							[style.amigoPravalerContainer]: amigoPravaler,
						})}
					>
						{customHeader ? (
							customHeaderVariant(customHeader)
						) : (
							<>
								{isMobile && btnBackMenu && (
									<button className={style.back} onClick={router.back}>
										<Arrow className={style.arrow} />
									</button>
								)}

								{amigoPravaler ? (
									<div className={style.logosRow}>
										<div className={style.logo}>
											<figure>
												<picture>
													<img loading="lazy" src={LogoAmigoEdu} alt="logo do Amigo Edu" />
												</picture>
											</figure>
										</div>

										<span className={style.logosPlus}>+</span>

										<div className={style.logo}>
											<figure>
												<picture>
													<img loading="lazy" src={LogoPravaler} alt="logo do Pravaler" />
												</picture>
											</figure>
										</div>
									</div>
								) : (
									<a
										href="https://amigoedu.com.br/"
										className={style.logo}
										onClick={() => {
											setMenu(false);
										}}
									>
										<figure>
											<picture>
												<img loading="lazy" src={LogoAmigoEdu} alt="" />
											</picture>
										</figure>
									</a>
								)}

								{!isMobile && !basicMode && <div className={style.linksMenu}>{menuNav}</div>}

								<>
									{!amigoPravaler &&
										!basicMode &&
										myInterests() &&
										hasWindow &&
										!window.location.pathname.includes("busca-nota") && (
											<div onWheel={() => setWasScrolled(true)}>
												<Popover
													title={titlePopover}
													trigger="hover"
													placement="bottom"
													visible={hovered}
													onVisibleChange={handleHoverChange}
												>
													<Popover
														content={
															<div className={style.popoverContent}>
																{content}
																<a onClick={hide} className={style.close}>
																	<Close />
																</a>
															</div>
														}
														trigger="click"
														placement="bottom"
														visible={clicked}
														onVisibleChange={handleClickChange}
													>
														<div className={`${[btnBackMenu && isMobile && style.hidden, style.myInterests].join(" ")}`}>
															{checkouts?.checkouts?.length >= 1 && <div className={style.hasInterests}></div>}
															<Bookmark />
														</div>
													</Popover>
												</Popover>
											</div>
										)}

									{!hideLoginMenu && !basicMode && (
										<nav className={style.menu}>
											<button
												onClick={() => {
													handleTrigger();
													setActive(true);
													setFixed(true);
												}}
												className={classNames(style.hamburger, {
													[style.open]: menu,
												})}
											>
												<span></span>
												<span></span>
												<span></span>
											</button>

											<ul
												className={classNames(style.menu, {
													[style.active]: menu,
												})}
											>
												<li className={style.myaccount}>
													<div className={style.respMenu}>
														<a
															onClick={() => {
																handleTrigger();
																closeFixed();
															}}
														>
															<button className={classNames(style.hamburger, style.open)}>
																<span></span>
																<span></span>
																<span></span>
															</button>
															<p className={style.text}>Fechar menu</p>
														</a>
														<div className={style.nav}>{menuNav}</div>
													</div>

													{!amigoPravaler &&
														(!isLoggedIn ? (
															<a
																data-cy="signin"
																className={style.btn}
																onClick={() => {
																	if (
																		!(
																			location.pathname.includes("/login") ||
																			location.pathname.includes("/registro")
																		)
																	) {
																		onClickShowModal(true);
																		setShowSignInForm(false);
																		setMenu(false);
																		closeFixed();
																	}
																}}
															>
																<SignInAlt />
																<span>Entrar</span>
															</a>
														) : (
															dataMe?.me && (
																<>
																	<Dropdown
																		overlay={menuFloating}
																		trigger={["click"]}
																		className={style.dropdown}
																		placement="bottomRight"
																	>
																		<div>
																			{user(dataMe)}
																			<a className="ant-dropdown-link">
																				<Arrow className={style.arrowDown} />
																			</a>
																		</div>
																	</Dropdown>
																	<div className={style.menuFloatingDrop}>{menuFloating}</div>
																</>
															)
														))}
												</li>
											</ul>
										</nav>
									)}
								</>
							</>
						)}
					</div>
				</div>
			</header>

			{showBuscaNotaBanners() && !inEnaePage && !inContestPage && !inBuscaNotaPage && !getUserEnemScore() && (
				<PromotionBar
					backgroundColor="#2D5098"
					color="#FFFFFF"
					hideAfterScroll={hideAfterScroll}
					onSubmit={submitHandler}
					icon={<BuscaNotaIcon />}
					backgroundColorButton="#FBCD21"
					backgroundColorPicture="#FBCD21"
					theme="buscaNotaEnem"
				/>
			)}

			{isEstacioWeek() && (
				<PromotionBar
					promotionTitleDesktop="ESTÁCIO + AMIGO EDU"
					promotionTitleMobile="SUA HORA DE BRILHAR"
					promotionSubtitle="Amigo Edu e Estácio juntos com as melhores ofertas"
					backgroundColor="#0049D2"
					color="#FFFFFF"
					hideAfterScroll={hideAfterScroll}
					onSubmit={submitHandler}
					endDateStr={ESTACIO_END_DATE}
					icon={<EstacioWeekPercentIcon style={{ width: "30px", margin: 5 }} />}
					backgroundColorButton="#B6E21A"
					colorButton="#1C3272"
					backgroundColorPicture="#B6E21A"
					colorTime="#FFFFFF"
					subColorTime="#1C3272"
					titleColor="#FFFFFF"
					backgroundColorTimer="#FFFFFF"
					theme="semana-estacio"
					isButtonVisible={false}
					hasDifferentClockColor={hasDifferentClockColor}
					mobileClockColor="#FFFFFF"
				/>
			)}
		</>
	);
};
